import React, { Component, useState } from 'react';
import { inject, observer } from 'mobx-react';
import styled from 'styled-components';
import {
  BorderedTab,
  BorderedTabs,
  Box,
  Button,
  ButtonGroup,
  Callout,
  Flex,
  FlexColumn,
  Heading,
  Icon,
  LinkButton,
  Text
} from 'core/components';
import Markdown from 'app/components/markdown/Markdown';
import MarkdownCodeViewer, { buildUnifiedDiff } from 'app/views/settings/ai/MarkdownCodeViewer';
import { markdownCodeStyles } from 'app/views/settings/ai/sharedStyles';
import ToolResultCard from '../components/ToolResultCard';
import { isShowingApprovalPreview } from '../util/toolResultAvailability';

const CARD_CLASS = 'tool-result-runbooks';

// Same view modes and styling as the Custom Network Context view, so runbook diffs feel familiar.
const VIEW_MODES = ['markdown', 'formatted', 'diff'];
const MARKDOWN_MODE = { name: 'markdown', highlightFormatting: true };

const MarkdownContainer = styled(Box)`
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.body};

  ${markdownCodeStyles}
`;

const UpdateInstructionsPreview = ({ instructions, existingInstructions }) => {
  const hasExisting = existingInstructions !== null;
  const [viewMode, setViewMode] = useState(hasExisting ? 'diff' : 'formatted');

  let content;
  if (viewMode === 'markdown') {
    content = <MarkdownCodeViewer value={instructions || ''} mode={MARKDOWN_MODE} minHeight="250px" />;
  } else if (viewMode === 'diff' && existingInstructions === instructions) {
    content = (
      <Callout intent="primary" icon="info-sign" title="No Changes Detected">
        The instructions are identical to the current version of this runbook.
      </Callout>
    );
  } else if (viewMode === 'diff') {
    content = (
      <MarkdownCodeViewer
        value={buildUnifiedDiff(existingInstructions, instructions)}
        mode={MARKDOWN_MODE}
        minHeight="250px"
        isDiff
      />
    );
  } else {
    content = (
      <MarkdownContainer className="markdown-body">
        <Markdown hideCsvDownloadButton>{instructions || ''}</Markdown>
      </MarkdownContainer>
    );
  }

  return (
    <FlexColumn gap={2} flex={1}>
      <ButtonGroup>
        {VIEW_MODES.map((mode) => (
          <Button
            key={mode}
            active={viewMode === mode}
            intent={viewMode === mode ? 'primary' : 'none'}
            disabled={mode === 'diff' && !hasExisting}
            onClick={() => setViewMode(mode)}
          >
            {mode.charAt(0).toUpperCase() + mode.slice(1)}
          </Button>
        ))}
      </ButtonGroup>
      {content}
    </FlexColumn>
  );
};

@inject('$runbook', '$kaizen')
@observer
class RunbookToolResult extends Component {
  state = { selectedTabId: null };

  handleTabChange = (id) => {
    this.setState({ selectedTabId: id });
  };

  get cardProps() {
    const { tool, onCloseToolResult, isInlineMode = false } = this.props;
    return { tool, onCloseToolResult, className: CARD_CLASS, isInlineMode };
  }

  renderViewRunbookLink(runbookName) {
    const { $runbook, $kaizen } = this.props;
    // the AI Advisor keeps settings within its own UI (panel or inline); the copilot on other pages uses the settings area
    const isInAiAdvisor = window.location.pathname.startsWith($kaizen.baseUrl);
    const baseUrl = isInAiAdvisor ? `${$kaizen.settingsBaseUrl}/runbooks` : $runbook.baseUrl;
    const to = runbookName ? `${baseUrl}?search=${encodeURIComponent(runbookName)}` : baseUrl;
    return (
      <LinkButton small icon="book" minimal to={to}>
        View Runbook
      </LinkButton>
    );
  }

  render() {
    const { tool } = this.props;
    const { selectedTabId } = this.state;
    const { raw_result } = tool;

    // Approval preview (create / update awaiting or rejected/timed-out)
    if (isShowingApprovalPreview(tool)) {
      const { action, names, new_name, instructions } = tool.query_object;
      // Nullish fallback: an explicit empty string means the update clears the description.
      const description = tool.query_object.description ?? tool.query_object.existing_description ?? '';
      const name = Array.isArray(names) && names.length > 0 ? names[0] : null;
      const isRename = new_name && new_name !== name;
      const actionLabel = action === 'create' ? 'Create' : 'Update';

      const existingInstructions = tool.query_object?.existing_instructions ?? null;

      return (
        <ToolResultCard {...this.cardProps} title={`${actionLabel} Runbook`}>
          <FlexColumn px={2} pb={2} gap={2} overflow="auto" flex={1}>
            {/* Runbook name */}
            <Heading level={4} m={0} fontSize={16} fontWeight={600}>
              {isRename ? (
                <Flex alignItems="center" gap={1} flexWrap="wrap">
                  <Text muted style={{ textDecoration: 'line-through' }}>
                    {name}
                  </Text>
                  <Icon icon="arrow-right" size={12} color="muted" />
                  <span>{new_name}</span>
                </Flex>
              ) : (
                name || '(unnamed)'
              )}
            </Heading>

            {/* Description */}
            {description && (
              <Text muted small>
                {description}
              </Text>
            )}

            <Box borderBottom="thin" />

            {/* Instructions — CNC-style Markdown/Formatted/Diff views for updates, plain Markdown for creates */}
            {action === 'update' ? (
              <UpdateInstructionsPreview instructions={instructions} existingInstructions={existingInstructions} />
            ) : (
              instructions && <Markdown>{instructions}</Markdown>
            )}
          </FlexColumn>
        </ToolResultCard>
      );
    }

    // Success state — raw_result is an array for get, a single object for create/update
    let runbooks = [];
    if (Array.isArray(raw_result)) {
      runbooks = raw_result;
    } else if (raw_result) {
      runbooks = [raw_result];
    }

    if (runbooks.length > 0) {
      const selectedRunbook = runbooks.find((r, index) => (r.id ?? index) === selectedTabId) || runbooks[0];
      const title = runbooks.length === 1 ? selectedRunbook.name : tool.title || 'Runbooks';

      return (
        <ToolResultCard
          {...this.cardProps}
          title={title}
          overflow="hidden"
          headerActions={this.renderViewRunbookLink(selectedRunbook?.name)}
        >
          <FlexColumn gap={0} height="100%" overflow="hidden">
            {runbooks.length > 1 && (
              <Flex p={2} pt={0} pb={1} borderBottom="thin">
                <BorderedTabs selectedTabId={selectedTabId ?? runbooks[0].id ?? 0} onChange={this.handleTabChange}>
                  {runbooks.map((runbook, index) => (
                    <BorderedTab id={runbook.id ?? index} key={runbook.id ?? index} title={runbook.name} />
                  ))}
                </BorderedTabs>
              </Flex>
            )}

            <FlexColumn gap={2} p={2} overflow="auto" flex={1}>
              {selectedRunbook.description && (
                <Text muted small>
                  {selectedRunbook.description}
                </Text>
              )}
              <Markdown>{selectedRunbook.instructions || ''}</Markdown>
            </FlexColumn>
          </FlexColumn>
        </ToolResultCard>
      );
    }

    return (
      <ToolResultCard {...this.cardProps} title={tool.title || 'Runbooks'} headerActions={this.renderViewRunbookLink()}>
        <FlexColumn p={2} gap={2}>
          <Text muted>No runbooks retrieved</Text>
        </FlexColumn>
      </ToolResultCard>
    );
  }
}

export default RunbookToolResult;
